import { trace } from '@opentelemetry/api';
import { onCLS, onINP, onLCP } from 'web-vitals';

import type { Metric } from 'web-vitals';

/**
 * Core Web Vitals（LCP / CLS / INP）を OpenTelemetry の trace として記録する計装。
 *
 * ## なぜ document-load span への setAttribute ではなく専用 span なのか
 * @opentelemetry/instrumentation-document-load は Navigation Timing / PerformanceResourceTiming
 * ベースであり、Core Web Vitals（Web Vitals API 独自の指標）は span 属性に含まれない
 * （instrumentation のソースを確認済み。documentLoad / documentFetch / resourceFetch span と
 * FP/FCP の paint イベントのみ）。
 * さらに LCP / CLS / INP は document-load span の終了後（ページライフサイクル後半）に確定する:
 *   - LCP: 最初のユーザー操作 or ページ非表示（visibilitychange / pagehide）時
 *   - CLS: ページ非表示時に累積値が確定
 *   - INP: ページ非表示時に最終値が確定
 * 既に end() 済みの span に setAttribute はできないため、値が確定した各コールバック受信時に
 * 短命の専用 span（"web-vitals <NAME>"）を生成し、属性を載せて即 end する。この span は
 * BatchSpanProcessor 経由で api-server のテレメトリ受け口 → X-Ray に送られ、trace 属性として
 * 記録される（Issue #400 の完了基準「Core Web Vitals が trace 属性として記録される」を満たす）。
 *
 * ## reportAllChanges を使わない理由
 * onLCP / onCLS / onINP の opts は既定（reportAllChanges: false）とし、確定値のみを 1 メトリクス
 * につき 1 span で記録する。true にすると layout shift やインタラクションのたびにコールバックが
 * 発火して span 数が急増し、X-Ray のコスト・ノイズが増大するため採用しない。
 *
 * ## PII について
 * metric.entries には LCP 要素のセレクタや画像リソース URL 等 PII になり得る情報が含まれるため
 * 属性には一切載せない。載せるのは name / value / rating / delta / id（重複排除用の不透明 ID） /
 * navigationType のみ。
 */

/** web-vitals 属性のキープレフィックス。 */
const WEB_VITALS_ATTR = {
  name: 'web_vitals.name',
  value: 'web_vitals.value',
  rating: 'web_vitals.rating',
  delta: 'web_vitals.delta',
  id: 'web_vitals.id',
  navigationType: 'web_vitals.navigation_type',
} as const;

/**
 * 確定した 1 メトリクスに対して短命 span を生成し、属性を載せて即 end する。
 * span の生存期間は同期的にゼロ（生成 → 属性設定 → end）。値の発生時刻を表す指標のため
 * duration には意味を持たせない。
 */
function recordMetric(metric: Metric): void {
  // trace.getTracer は otel-init.ts の provider.register() で登録済みのグローバル provider から
  // tracer を取得する。registerWebVitals() は initOtel() の後（provider 登録後）に呼ばれる。
  const tracer = trace.getTracer('web-vitals');
  const span = tracer.startSpan(`web-vitals ${metric.name}`, {
    attributes: {
      [WEB_VITALS_ATTR.name]: metric.name,
      [WEB_VITALS_ATTR.value]: metric.value,
      [WEB_VITALS_ATTR.rating]: metric.rating,
      [WEB_VITALS_ATTR.delta]: metric.delta,
      [WEB_VITALS_ATTR.id]: metric.id,
      [WEB_VITALS_ATTR.navigationType]: metric.navigationType,
    },
  });
  // 属性は startSpan の options で確定済みのため即座に end する。
  span.end();
}

/**
 * Core Web Vitals（LCP / CLS / INP）の購読を開始する。
 * otel-init.ts の initOtel() から（provider 登録後・useMock スキップの内側で）一度だけ呼ぶ。
 *
 * 各メトリクスは値が確定した時点で recordMetric により専用 span として記録される。
 * onLCP / onCLS / onINP は opts 既定（reportAllChanges: false）で購読する。
 */
export function registerWebVitals(): void {
  onLCP(recordMetric);
  onCLS(recordMetric);
  onINP(recordMetric);
}
